/**
 * Small pieces the Workflows pages share: a run's status pill and the panel
 * over the chart (AmoregGTM's step inspector, top right) that edits a step
 * (while editing) or shows what it did (on a run).
 *
 * @module components/workflows/workflowParts
 */
import {
  WORKFLOW_LIMITS,
  type BrowserTaskSummary,
  type WorkflowGraph,
  type WorkflowNode,
  type WorkflowNodeType,
  type WorkflowPromptEffort,
  type WorkflowPromptModel,
  type WorkflowRun,
  type WorkflowStepAttempt,
  type WorkflowStepRun,
} from "@amoreg/contracts";
import { Link } from "@tanstack/react-router";
import { CheckIcon, CopyIcon, CopyPlusIcon, ExternalLinkIcon, MessageSquareIcon, PlusIcon, RotateCcwIcon, Trash2Icon } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "~/lib/utils";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";
import { browserTaskChoices } from "./BrowserTasks.logic";
import { ZoomInIcon } from "./canvas/icons";
import { KINDS, kindClass } from "./canvas/kinds";
import { CompareStepEditor, TablePicker } from "./CompareParts";
import { compareTableSources } from "./CompareParts.logic";
import { currentAgentEffort } from "@repo/shared/agentModels";
import { DeliveryLink, DestinationStepEditor } from "./DestinationParts";
import { PeoplePicker, type MemberChoice } from "./PeoplePicker";
import { IngestionStepEditor, IngestionStepFields } from "./IngestionFields";
import { StepTables } from "./StepTables";
import { IntakeStepEditor } from "./IntakeSources";
import {
  EFFORT_CHOICES,
  MODEL_CHOICES,
  RUN_STATUS_LABEL,
  stepStatusLabel,
  stepDuration,
} from "./workflowCanvas.logic";

const RUN_PILL: Readonly<Record<WorkflowRun["status"], string>> = {
  queued: "bg-info/12 text-info-foreground",
  running: "bg-info/12 text-info-foreground",
  waiting: "bg-warning/12 text-warning-foreground",
  succeeded: "bg-success/12 text-success-foreground",
  failed: "bg-destructive/10 text-destructive-foreground",
  cancelled: "bg-muted text-muted-foreground",
};

export function RunStatusPill({ status, className }: { readonly status: WorkflowRun["status"]; readonly className?: string }) {
  return (
    <span
      data-workflow-run-status={status}
      className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium", RUN_PILL[status], className)}
    >
      {RUN_STATUS_LABEL[status]}
    </span>
  );
}

/**
 * GTM's step inspector: floating top right over the canvas, the step's kind
 * in its accent above its name, a close (the zoom-in plus, turned 45°), the
 * body fading in per step, and an optional footer.
 */
function PanelFrame({
  id,
  title,
  type,
  onClose,
  footer,
  children,
}: {
  readonly id: string;
  readonly title: string;
  readonly type: WorkflowNodeType;
  readonly onClose: () => void;
  readonly footer?: ReactNode;
  readonly children: React.ReactNode;
}) {
  const meta = KINDS[type];
  const Icon = meta.icon;
  return (
    <aside
      aria-label={`${meta.label} step`}
      data-canvas-overlay
      onPointerDown={(event) => event.stopPropagation()}
      className={cn(
        kindClass(type),
        "absolute top-4 right-4 z-30 flex max-h-[calc(100%-180px)] min-h-[260px] w-[min(480px,calc(100%-32px))] flex-col overflow-clip rounded-lg bg-canvas-surface shadow-canvas-popover transition-[opacity,translate] duration-200 ease-power3-out motion-reduce:transition-none starting:translate-x-3 starting:opacity-0",
      )}
      data-workflow-panel=""
    >
      <div className="flex shrink-0 items-center justify-between gap-3 px-[18px] py-3.5">
        <div className="min-w-0 flex-1 px-1">
          <p className="flex items-center gap-1.5 text-[12px] leading-5 font-medium text-kind">
            <Icon aria-hidden className="size-4" />
            {meta.label}
          </p>
          <p className="truncate text-[16px] leading-7 font-semibold text-foreground">{title}</p>
        </div>
        <Button size="icon-sm" variant="ghost" onClick={onClose} aria-label="Close panel">
          <ZoomInIcon className="size-[18px] rotate-45 text-muted-foreground" />
        </Button>
      </div>
      <div className="h-px shrink-0 bg-(--canvas-line)" />
      <div key={id} className="min-h-0 flex-1 animate-canvas-fade-in space-y-4 overflow-y-auto px-5 py-[18px]">
        {children}
      </div>
      {footer ? (
        <>
          <div className="h-px shrink-0 bg-(--canvas-line)" />
          <div className="shrink-0 px-[18px] py-4">{footer}</div>
        </>
      ) : null}
    </aside>
  );
}

/**
 * The chat model and reasoning effort an AI step (Prompt, an asking
 * Condition, Ingestion) runs on; "Default" leaves the effort unset.
 */
function ModelPicker<C extends { readonly model: WorkflowPromptModel; readonly effort?: WorkflowPromptEffort | undefined }>({
  config,
  onChange,
}: {
  readonly config: C;
  readonly onChange: (next: C) => void;
}) {
  const effort = config.effort === undefined ? undefined : currentAgentEffort(config.effort);
  return (
    <div className="grid grid-cols-2 gap-3">
      <label className="block space-y-1.5">
        <span className="text-xs font-medium">Model</span>
        <Select value={config.model} onValueChange={(value) => onChange({ ...config, model: value === "pro" ? "pro" : "standard" })}>
          <SelectTrigger size="sm" aria-label="Model">
            <span className="truncate text-xs">{MODEL_CHOICES.find((choice) => choice.value === config.model)?.label}</span>
          </SelectTrigger>
          <SelectContent>
            {MODEL_CHOICES.map((choice) => (
              <SelectItem key={choice.value} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <label className="block space-y-1.5">
        <span className="text-xs font-medium">Reasoning</span>
        <Select
          value={effort ?? "default"}
          onValueChange={(value) => {
            const effort = EFFORT_CHOICES.find((choice) => choice.value === value)?.value;
            const { effort: _previous, ...rest } = config;
            onChange((effort ? { ...rest, effort } : rest) as C);
          }}
        >
          <SelectTrigger size="sm" aria-label="Reasoning">
            <span className="truncate text-xs">{EFFORT_CHOICES.find((choice) => choice.value === effort)?.label ?? "Default"}</span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default</SelectItem>
            {EFFORT_CHOICES.map((choice) => (
              <SelectItem key={choice.value} value={choice.value}>
                {choice.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
    </div>
  );
}

const ALLOWANCE_NOTE = "Runs as the person who presses Run, on the same models as chat, and counts toward their allowance.";

/** A member who can be named as an approver or a recipient. */
export type ApproverChoice = MemberChoice;

/** Said under a step's text where, as in a Prompt's instructions, `{{…}}` picks what reaches it. */
const PLACEHOLDER_HINT = "Without any {{…}}, the step gets the run's input and every earlier step's result after your instructions.";

/** A Browser task step's editor: which task, and the values it needs. */
function BrowserTaskStepFields({
  node,
  onChange,
  browserTasks,
  onOpenBrowserTask,
}: {
  readonly node: Extract<WorkflowNode, { type: "browser-task" }>;
  readonly onChange: (next: WorkflowNode) => void;
  readonly browserTasks: ReadonlyArray<BrowserTaskSummary> | null;
  readonly onOpenBrowserTask: (taskId: string | null) => void;
}) {
  const choices = browserTaskChoices(browserTasks ?? []);
  const taskId = node.config.taskId;
  const chosen = choices.find((choice) => choice.id === taskId) ?? null;
  const chosenLabel = !taskId
    ? browserTasks === null
      ? "Loading your browser tasks…"
      : "Choose a browser task"
    : (chosen?.name ?? (browserTasks === null ? "Loading…" : "A browser task you cannot use"));
  return (
    <>
      <div className="block space-y-1.5">
        <span className="text-xs font-medium">Browser task</span>
        <Select
          value={taskId}
          onValueChange={(value) => onChange({ ...node, config: { ...node.config, taskId: typeof value === "string" ? value : "" } })}
        >
          <SelectTrigger size="sm" aria-label="Browser task" data-workflow-browser-task="">
            <span className={cn("truncate text-xs", !chosen && "text-muted-foreground")}>{chosenLabel}</span>
          </SelectTrigger>
          <SelectContent>
            {choices.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">No browser tasks yet.</p>
            ) : (
              choices.map((choice) => (
                <SelectItem key={choice.id} value={choice.id} disabled={choice.unavailable !== null}>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{choice.name}</span>
                    <span className="text-[11px] text-muted-foreground">
                      {choice.unavailable ?? (choice.scope === "organization" ? "Organisation" : "Personal")}
                    </span>
                  </span>
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
        <div className="flex flex-wrap gap-1.5">
          {taskId ? (
            <Button size="xs" variant="outline" onClick={() => onOpenBrowserTask(taskId)} data-workflow-open-browser-task="">
              <ExternalLinkIcon />
              Open task
            </Button>
          ) : null}
          <Button size="xs" variant="ghost" onClick={() => onOpenBrowserTask(null)}>
            <PlusIcon />
            New browser task
          </Button>
        </div>
      </div>
      <label className="block space-y-1.5">
        <span className="text-xs font-medium">Values for the task</span>
        <Textarea
          rows={5}
          value={node.config.inputs}
          maxLength={20_000}
          placeholder="e.g. Notices published in the week of {{input}}"
          onChange={(event) => onChange({ ...node, config: { ...node.config, inputs: event.currentTarget.value } })}
          data-workflow-browser-task-inputs=""
          data-step-field="config.inputs"
        />
        <span className="block text-[11px] leading-snug text-muted-foreground">{PLACEHOLDER_HINT}</span>
      </label>
      <p className="text-[11px] leading-snug text-muted-foreground">
        Runs in your own chat with your saved logins. Reads and downloads only; up to 40 browser actions and 30 minutes.
      </p>
    </>
  );
}

const CONDITION_MODES = [
  { value: "ask", label: "Ask the AI" },
  { value: "contains", label: "Text contains" },
] as const;

/** Edits one step while the chart is being edited. */
export function StepEditorPanel({
  node,
  onChange,
  onDelete,
  onClose,
  canDelete,
  browserTasks,
  onOpenBrowserTask,
  members = null,
  organizationId = null,
  workflowId = null,
  spaceId = null,
  graph = null,
  footer,
}: {
  readonly node: WorkflowNode;
  /** The chart being edited, for a Compare step's choice of earlier tables. */
  readonly graph?: WorkflowGraph | null;
  /** Below the settings: walk on to the next step, or add one. */
  readonly footer?: ReactNode;
  readonly onChange: (next: WorkflowNode) => void;
  readonly onDelete: () => void;
  readonly onClose: () => void;
  readonly canDelete: boolean;
  /** The viewer's browser tasks; null until loaded. */
  readonly browserTasks: ReadonlyArray<BrowserTaskSummary> | null;
  /** Opens a browser task in its dialog; null for a new one. */
  readonly onOpenBrowserTask: (taskId: string | null) => void;
  /** The organization's members, for an Approval step's approvers; null when they cannot be read. */
  readonly members?: ReadonlyArray<ApproverChoice> | null;
  /** Where an Ingestion step asks for field suggestions; null hides them. */
  readonly organizationId?: string | null;
  /** The workflow's space, where an Intake step's new Space files source starts. */
  readonly spaceId?: string | null;
  /** The workflow being edited, for an email source's mailbox permission. */
  readonly workflowId?: string | null;
}) {
  return (
    <PanelFrame id={node.id} title={node.label} type={node.type} onClose={onClose} footer={footer}>
      <label className="block space-y-1.5">
        <span className="text-xs font-medium">Name</span>
        <Input
          value={node.label}
          maxLength={120}
          onChange={(event) => onChange({ ...node, label: event.currentTarget.value })}
          data-step-field="label"
        />
        <span className="block text-[11px] text-muted-foreground">
          Later prompts can use this step's result as {"{{"}
          {node.label.trim() || "Step name"}
          {"}}"}
          {node.type === "ingestion" ? (
            <>
              , and each field as {"{{"}
              {node.label.trim() || "Step name"}.Field name{"}}"}
            </>
          ) : null}
          .
        </span>
      </label>
      {node.type === "start" ? (
        <label className="block space-y-1.5">
          <span className="text-xs font-medium">What should Run ask for?</span>
          <Textarea
            rows={3}
            value={node.config.inputHint ?? ""}
            maxLength={500}
            placeholder="e.g. The client's name and the quarter to review"
            onChange={(event) => onChange({ ...node, config: { ...node.config, inputHint: event.currentTarget.value } })}
            data-step-field="config.inputHint"
          />
          <span className="block text-[11px] text-muted-foreground">Prompts can use what was typed as {"{{input}}"}.</span>
        </label>
      ) : null}
      {node.type === "start" ? (
        <div className="space-y-1.5" data-workflow-start-files="">
          <label className="flex items-center gap-2 text-[12px]">
            <Switch
              checked={node.config.files?.ask === true}
              onCheckedChange={(ask) => onChange({ ...node, config: { ...node.config, files: { ask, hint: node.config.files?.hint ?? "" } } })}
              data-workflow-start-files-ask=""
            />
            <span className="font-medium">Ask for files when it runs</span>
          </label>
          {node.config.files?.ask ? (
            <Input
              value={node.config.files.hint}
              maxLength={200}
              placeholder="e.g. Today's manager trade file"
              onChange={(event) =>
                onChange({ ...node, config: { ...node.config, files: { ask: true, hint: event.currentTarget.value } } })
              }
              className="text-xs"
              data-workflow-start-files-hint=""
              data-step-field="config.files"
            />
          ) : null}
          <span className="block text-[11px] leading-snug text-muted-foreground">
            The Run dialog then takes up to 3 files (PDF, Word, Excel, CSV, text). Later steps read them after the input, and
            spreadsheets come through as tables for Compare, For each row or Excel. A scheduled run has no files.
          </span>
        </div>
      ) : null}
      {node.type === "prompt" ? (
        <>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Instructions</span>
            <Textarea
              rows={9}
              value={node.config.instructions}
              maxLength={20_000}
              placeholder="e.g. From {{input}}, list every deadline mentioned, with its date and what is due."
              onChange={(event) => onChange({ ...node, config: { ...node.config, instructions: event.currentTarget.value } })}
              data-workflow-instructions=""
              data-step-field="config.instructions"
            />
            <span className="block text-[11px] leading-snug text-muted-foreground">{PLACEHOLDER_HINT}</span>
          </label>
          <ModelPicker config={node.config} onChange={(config) => onChange({ ...node, config })} />
          <p className="text-[11px] leading-snug text-muted-foreground">{ALLOWANCE_NOTE}</p>
        </>
      ) : null}
      {node.type === "browser-task" ? (
        <BrowserTaskStepFields
          node={node}
          onChange={onChange}
          browserTasks={browserTasks}
          onOpenBrowserTask={onOpenBrowserTask}
        />
      ) : null}
      {node.type === "condition" ? (
        <>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Decide by</span>
            <Select
              value={node.config.mode}
              onValueChange={(value) =>
                onChange({ ...node, config: { ...node.config, mode: value === "contains" ? "contains" : "ask" } })
              }
            >
              <SelectTrigger size="sm" aria-label="Decide by">
                <span className="truncate text-xs">{CONDITION_MODES.find((choice) => choice.value === node.config.mode)?.label}</span>
              </SelectTrigger>
              <SelectContent>
                {CONDITION_MODES.map((choice) => (
                  <SelectItem key={choice.value} value={choice.value}>
                    {choice.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          {node.config.mode === "contains" ? (
            <label className="block space-y-1.5">
              <span className="text-xs font-medium">Text to look for</span>
              <Input
                value={node.config.text}
                maxLength={500}
                placeholder="e.g. breach"
                onChange={(event) => onChange({ ...node, config: { ...node.config, text: event.currentTarget.value } })}
                data-workflow-condition-text=""
                data-step-field="config.text"
              />
              <span className="block text-[11px] leading-snug text-muted-foreground">
                Yes when the result of a step linked into this one contains it, ignoring case. No otherwise.
              </span>
            </label>
          ) : (
            <>
              <label className="block space-y-1.5">
                <span className="text-xs font-medium">Question</span>
                <Textarea
                  rows={5}
                  value={node.config.question}
                  maxLength={20_000}
                  placeholder="e.g. Does {{input}} mention a deadline in the next 30 days?"
                  onChange={(event) => onChange({ ...node, config: { ...node.config, question: event.currentTarget.value } })}
                  data-workflow-condition-question=""
                  data-step-field="config.question"
                />
                <span className="block text-[11px] leading-snug text-muted-foreground">
                  Ask a yes-or-no question. The AI reads the run's input and earlier results and answers Yes or No.
                </span>
              </label>
              <ModelPicker config={node.config} onChange={(config) => onChange({ ...node, config })} />
            </>
          )}
          <p className="text-[11px] leading-snug text-muted-foreground">
            Link the Yes outlet and the No outlet to the steps each answer leads to. Steps on the other side are skipped.
          </p>
        </>
      ) : null}
      {node.type === "compare" ? <CompareStepEditor node={node} graph={graph} onChange={onChange} /> : null}
      {node.type === "for-each" ? (
        <>
          <TablePicker
            label="Go through the rows of"
            side="left"
            value={node.config.source}
            sources={graph ? compareTableSources(graph, node.id) : []}
            onChange={(source) => onChange({ ...node, config: { ...node.config, source } })}
          />
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">For each row</span>
            <Textarea
              rows={6}
              value={node.config.instructions}
              maxLength={WORKFLOW_LIMITS.instructionsLength}
              placeholder="e.g. Find out why {{row.Ticker}} moved {{row.Difference}} today, from the news."
              onChange={(event) => onChange({ ...node, config: { ...node.config, instructions: event.currentTarget.value } })}
              data-workflow-for-each-instructions=""
              data-step-field="config.instructions"
            />
            <span className="block text-[11px] leading-snug text-muted-foreground">
              Use {"{{row.Column}}"} for a value of the row. The answers come back as a table: each row with its Answer, for
              Save to space, Compare or a checker.
            </span>
          </label>
          <label className="flex items-center gap-2 text-[12px]">
            <span className="font-medium">At most</span>
            <Input
              type="number"
              min={1}
              max={50}
              value={node.config.maxRows}
              onChange={(event) => {
                const rows = Math.round(Number(event.currentTarget.value));
                if (Number.isFinite(rows)) onChange({ ...node, config: { ...node.config, maxRows: Math.min(50, Math.max(1, rows)) } });
              }}
              className="w-16 text-xs"
              data-workflow-for-each-max=""
              data-step-field="config.maxRows"
            />
            <span>rows, from the top (50 at most)</span>
          </label>
          <ModelPicker config={node.config} onChange={(config) => onChange({ ...node, config })} />
          <p className="text-[11px] leading-snug text-muted-foreground">
            One AI turn goes through every row, billed as one Prompt step. {ALLOWANCE_NOTE}
          </p>
        </>
      ) : null}
      {node.type === "approval" ? (
        <>
          <PeoplePicker
            label="Who approves"
            role="approver"
            people={node.config.approverEmails}
            members={members}
            limit={WORKFLOW_LIMITS.approvers}
            note="Any one of them can approve. The person who starts a run never approves their own, and being an admin is not enough."
            onChange={(approverEmails) => onChange({ ...node, config: { ...node.config, approverEmails: [...approverEmails] } })}
          />
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">What should they check?</span>
            <Textarea
              rows={4}
              value={node.config.instructions}
              maxLength={2_000}
              placeholder="e.g. The figures match the client file and the tone suits a partner letter."
              onChange={(event) => onChange({ ...node, config: { ...node.config, instructions: event.currentTarget.value } })}
              data-workflow-approval-instructions=""
              data-step-field="config.instructions"
            />
          </label>
          <p className="text-[11px] leading-snug text-muted-foreground">
            The run waits here, as long as it takes. Approvers find it in their Inbox, with the results linked into this step,
            and can approve, reject, or send it back to an earlier step with feedback.
          </p>
        </>
      ) : null}
      {node.type === "checklist" ? (
        <>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">What has to be done</span>
            <Textarea
              rows={4}
              value={node.config.instructions}
              maxLength={2_000}
              placeholder="e.g. Import the trade file into Capital Studios and check the totals match."
              onChange={(event) => onChange({ ...node, config: { ...node.config, instructions: event.currentTarget.value } })}
              data-workflow-checklist-instructions=""
              data-step-field="config.instructions"
            />
          </label>
          <PeoplePicker
            label="Who does it"
            role="approver"
            people={node.config.assigneeEmails}
            members={members}
            limit={WORKFLOW_LIMITS.approvers}
            note="Any one of them can mark it done. Name nobody, and the person who starts each run does it."
            onChange={(assigneeEmails) => onChange({ ...node, config: { ...node.config, assigneeEmails: [...assigneeEmails] } })}
          />
          <p className="text-[11px] leading-snug text-muted-foreground">
            The run waits here, as long as it takes, for work done outside Amoreg. It shows in their Inbox with the results
            linked into this step; they mark it done with an optional note, and who did it and when is kept with the run.
          </p>
        </>
      ) : null}
      {node.type === "ingestion" ? (
        <>
          <IngestionStepEditor node={node} organizationId={organizationId} onChange={onChange} />
          <ModelPicker config={node.config} onChange={(config) => onChange({ ...node, config })} />
          <p className="text-[11px] leading-snug text-muted-foreground">{ALLOWANCE_NOTE}</p>
        </>
      ) : null}
      {node.type === "intake" ? (
        <IntakeStepEditor node={node} organizationId={organizationId} workflowId={workflowId} spaceId={spaceId} onChange={onChange} />
      ) : null}
      {node.type === "chat" || node.type === "notify" || node.type === "save-file" || node.type === "email" ? (
        <DestinationStepEditor node={node} organizationId={organizationId} members={members} onChange={onChange} />
      ) : null}
      {node.type === "output" ? (
        <p className="text-[13px] leading-snug text-muted-foreground">
          Shows what reaches it on the run page: the output of each step linked into this one, each headed with its step's
          name when there are several. Optional: any step can end a workflow.
        </p>
      ) : null}
      {canDelete ? (
        <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={onDelete}>
          <Trash2Icon />
          Delete step
        </Button>
      ) : null}
    </PanelFrame>
  );
}

/** What a step did before it ran again (AMO-308): each attempt, newest first, its result folded away. */
function EarlierAttempts({ attempts }: { readonly attempts: ReadonlyArray<WorkflowStepAttempt> }) {
  return (
    <div className="space-y-1.5" data-workflow-earlier-attempts="">
      <p className="text-xs font-medium">Earlier attempts</p>
      <ul className="space-y-1">
        {attempts.map((entry) => (
          <li key={entry.attempt}>
            <details className="rounded-md bg-muted/40 px-2 py-1 text-[12px]">
              <summary className="cursor-pointer text-muted-foreground">
                Attempt {entry.attempt} · {entry.status === "failed" ? "failed" : "succeeded"}
                {entry.finishedAt ? ` · ${formatRelativeTimeLabel(entry.finishedAt)}` : ""}
              </summary>
              <p className="mt-1 whitespace-pre-wrap leading-relaxed text-foreground">
                {entry.errorMessage ?? entry.output ?? "Nothing to pass on."}
              </p>
            </details>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** What one step of a run did: its state, its result, or why it failed. */
export function StepRunPanel({
  node,
  step,
  now,
  onRetry,
  onRerunWithCurrent = null,
  retrying,
  onClose,
  org,
  children,
}: {
  readonly node: WorkflowNode;
  readonly step: WorkflowStepRun | null;
  /** Reruns from this step with the workflow's settings as they are now (AMO-308); null hides it. */
  readonly onRerunWithCurrent?: (() => void) | null;
  /** The organization's URL segment, for a Browser task step's chat link. */
  readonly org: string;
  readonly now: number;
  readonly onRetry: (() => void) | null;
  readonly retrying: boolean;
  readonly onClose: () => void;
  /** What only this kind of step shows on a run (an Approval step's decision). */
  readonly children?: ReactNode;
}) {
  const [showDetail, setShowDetail] = useState(false);
  const status = step?.status ?? "pending";
  const duration = step ? stepDuration(step, now) : null;
  return (
    <PanelFrame id={node.id} title={node.label} type={node.type} onClose={onClose}>
      <p className="text-[13px] text-foreground" data-workflow-panel-status={status}>
        {stepStatusLabel(status, node.type)}
        {duration ? <span className="text-muted-foreground"> · {duration}</span> : null}
        {step && step.attempt > 1 ? <span className="text-muted-foreground"> · attempt {step.attempt}</span> : null}
      </p>
      {children}
      {step?.error ? (
        <div className="space-y-2 rounded-lg border border-destructive/20 bg-destructive/5 p-3">
          <p className="text-[13px] text-foreground">{step.error.message}</p>
          {step.error.detail ? (
            <>
              <button
                type="button"
                className="text-[11px] font-medium text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setShowDetail((shown) => !shown)}
              >
                {showDetail ? "Hide details" : "Show details"}
              </button>
              {showDetail ? (
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-background p-2 text-[11px] text-muted-foreground">
                  {step.error.detail}
                </pre>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
      {step?.chatThreadId ? (
        <div className="space-y-1.5">
          <Button
            size="sm"
            variant="outline"
            render={<Link to="/$org/$threadId" params={{ org, threadId: step.chatThreadId }} />}
            data-workflow-open-chat=""
          >
            <MessageSquareIcon />
            Open chat
          </Button>
          <p className="text-[11px] leading-snug text-muted-foreground">
            The browser helper works in this chat: watch it there, or sign in when a site asks.
          </p>
        </div>
      ) : null}
      {onRetry ? (
        <div className="space-y-1.5" data-workflow-rerun="">
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" onClick={onRetry} disabled={retrying} data-workflow-rerun-here="">
              <RotateCcwIcon />
              {retrying ? "Starting…" : status === "failed" ? "Retry this step" : "Rerun from here"}
            </Button>
            {onRerunWithCurrent ? (
              <Button size="sm" variant="outline" onClick={onRerunWithCurrent} disabled={retrying} data-workflow-rerun-current="">
                With current settings
              </Button>
            ) : null}
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            This step and every step after it run again; earlier steps keep their results. With current settings, they
            run as the workflow is now, so a step fixed since this run started runs as fixed.
          </p>
        </div>
      ) : null}
      {step?.fields && step.status === "succeeded" ? <IngestionStepFields fields={step.fields} /> : null}
      {step?.tables && step.tables.length > 0 && step.status === "succeeded" ? <StepTables tables={step.tables} /> : null}
      {step?.delivery && step.status === "succeeded" ? <DeliveryLink delivery={step.delivery} org={org} /> : null}
      {step?.output ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium">Result</p>
          <pre
            className="max-h-[50vh] overflow-auto whitespace-pre-wrap rounded-lg border border-border bg-muted/40 p-3 font-sans text-[12.5px] leading-relaxed text-foreground"
            data-workflow-output=""
          >
            {step.output}
          </pre>
        </div>
      ) : status === "succeeded" ? (
        <p className="text-[13px] text-muted-foreground">This step finished with nothing to pass on.</p>
      ) : null}
      {step?.earlierAttempts && step.earlierAttempts.length > 0 ? <EarlierAttempts attempts={step.earlierAttempts} /> : null}
      {node.type === "prompt" ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium">Instructions it ran with</p>
          <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground">{node.config.instructions}</p>
        </div>
      ) : null}
      {node.type === "browser-task" && node.config.task ? (
        <div className="space-y-1.5">
          <p className="text-xs font-medium">Task it ran</p>
          <p className="text-[12px] text-foreground">
            {node.config.task.definition.name}
            <span className="text-muted-foreground"> · version {node.config.task.version}</span>
          </p>
          {node.config.inputs.trim() ? (
            <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-muted-foreground">{node.config.inputs}</p>
          ) : null}
        </div>
      ) : null}
    </PanelFrame>
  );
}

/** An AI step's model and reasoning, when it has them (Prompt, an asking Condition, Ingestion). */
function quickModel(node: WorkflowNode, onChange: (next: WorkflowNode) => void): ReactNode {
  if (node.type === "prompt" || node.type === "ingestion") {
    return <ModelPicker config={node.config} onChange={(config) => onChange({ ...node, config } as WorkflowNode)} />;
  }
  if (node.type === "condition" && node.config.mode === "ask") {
    return <ModelPicker config={node.config} onChange={(config) => onChange({ ...node, config })} />;
  }
  return null;
}

/**
 * The editor's hover card body: rename, the model and reasoning of an AI
 * step, Duplicate and Delete. Everything else is in the step's panel.
 */
export function StepQuickEdit({
  node,
  onChange,
  onDuplicate,
  onDelete,
}: {
  readonly node: WorkflowNode;
  readonly onChange: (next: WorkflowNode) => void;
  /** Null for Start, which a workflow has only one of. */
  readonly onDuplicate: (() => void) | null;
  readonly onDelete: (() => void) | null;
}) {
  return (
    <div className="space-y-3" data-workflow-quick-edit={node.id}>
      <label className="block space-y-1.5">
        <span className="text-xs font-medium">Name</span>
        <Input
          size="sm"
          value={node.label}
          maxLength={120}
          onChange={(event) => onChange({ ...node, label: event.currentTarget.value })}
          data-workflow-quick-name=""
          data-step-field="label"
        />
      </label>
      {quickModel(node, onChange)}
      {onDuplicate || onDelete ? (
        <div className="flex items-center gap-1.5">
          {onDuplicate ? (
            <Button size="xs" variant="outline" onClick={onDuplicate} data-workflow-quick-duplicate="">
              <CopyPlusIcon />
              Duplicate
            </Button>
          ) : null}
          {onDelete ? (
            <Button size="xs" variant="ghost" className="text-destructive hover:text-destructive" onClick={onDelete} data-workflow-quick-delete="">
              <Trash2Icon />
              Delete
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** A run's hover card body: how the step went, the start of its result, and Retry or Open chat. */
export function StepQuickRun({
  type,
  step,
  now,
  onRetry,
  retrying,
  org,
}: {
  /** The step's kind, for how its status reads. */
  readonly type: WorkflowNodeType;
  readonly step: WorkflowStepRun | null;
  readonly now: number;
  readonly onRetry: (() => void) | null;
  readonly retrying: boolean;
  /** The organization's URL segment, for a Browser task step's chat link. */
  readonly org: string;
}) {
  const [copied, setCopied] = useState(false);
  const status = step?.status ?? "pending";
  const duration = step ? stepDuration(step, now) : null;
  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="space-y-2.5" data-workflow-quick-run={status}>
      <p className="text-[13px] text-foreground">
        {stepStatusLabel(status, type)}
        {duration ? <span className="text-muted-foreground"> · {duration}</span> : null}
        {step && step.attempt > 1 ? <span className="text-muted-foreground"> · attempt {step.attempt}</span> : null}
      </p>
      {step?.error ? <p className="line-clamp-3 text-[12.5px] leading-snug text-destructive">{step.error.message}</p> : null}
      {step?.output ? (
        <div className="space-y-1.5">
          <p className="line-clamp-4 whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-[12px] leading-relaxed text-foreground" data-workflow-quick-output="">
            {step.output}
          </p>
        </div>
      ) : status === "succeeded" ? (
        <p className="text-[12.5px] text-muted-foreground">Finished with nothing to pass on.</p>
      ) : null}
      {step?.output || onRetry || step?.chatThreadId ? (
        <div className="flex flex-wrap items-center gap-1.5">
          {step?.output ? (
            <Button size="xs" variant="outline" onClick={() => copy(step.output ?? "")} data-workflow-quick-copy="">
              {copied ? <CheckIcon /> : <CopyIcon />}
              {copied ? "Copied" : "Copy result"}
            </Button>
          ) : null}
          {onRetry ? (
            <Button size="xs" onClick={onRetry} disabled={retrying} data-workflow-quick-retry="">
              <RotateCcwIcon />
              {retrying ? "Retrying…" : "Retry"}
            </Button>
          ) : null}
          {step?.chatThreadId ? (
            <Button size="xs" variant="outline" render={<Link to="/$org/$threadId" params={{ org, threadId: step.chatThreadId }} />}>
              <MessageSquareIcon />
              Open chat
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
